/* eslint-disable react/no-unused-prop-types */
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { add, format, isBefore, parseISO, startOfDay } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import * as R from 'ramda';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import { isLessThan, isNumberWithOptionalFormatting } from 'utils/helpers/formValidations';
import {
  DATE_GTE,
  DATE_GTE_NOW,
  GT_VAL,
  GTE_VAL,
  INTEGER,
  LT_VAL_CHAR,
  REQUIRED,
} from 'utils/helpers/validationMessages';
import { AuthContext } from 'authentication/AuthContext';
import renderInput from '../components/renderInput';
import DataDisplayItem from '../components/DataDisplayItem';

const infoContainerStyle = {
  display: 'grid',
  gap: { lg: 3, xs: 0 },
  gridTemplateColumns: { lg: '1fr' },
};

const EditShipment = ({
  hasAcceptPermissions,
  availableDate,
  bookingOriginOptions,
  isFetchingBookingOrigins,
  onEditPage,
  origin,
  setAreAllUpdatesValid,
  setShipmentUpdates,
  shipmentUpdates,
  totalDistributedVolume,
  isDistributionsVolumeInvalid,
  shipperName,
  status,
  batchOwnerName,
}) => {
  const shipmentUpdatesProp = R.prop(R.__, shipmentUpdates);

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const { affiliates } = useContext(AuthContext);

  const isBatchOwner = affiliates.some(affiliate =>
    R.propEq('customerNameShort', batchOwnerName, affiliate)
  );

  const hasAffiliateShipper = affiliates.some(affiliate =>
    R.propEq('customerNameShort', shipperName, affiliate)
  );

  const isRequestDateDisabled =
    (!hasAffiliateShipper || R.equals('Unconfirmed', status)) && !isBatchOwner;

  useEffect(() => {
    if (bookingOriginOptions.length > 0) {
      const originLocationName =
        bookingOriginOptions.length > 0
          ? bookingOriginOptions.find(
              originLoc =>
                originLoc.bookingOriginId === shipmentUpdates.bookingOrigin.bookingOriginId
            )?.defaultLocationName
          : shipmentUpdatesProp('locationName');
      setShipmentUpdates(R.assoc('locationName', originLocationName, shipmentUpdates));
    }
  }, [bookingOriginOptions, shipmentUpdatesProp, setShipmentUpdates, shipmentUpdates]);

  const validateVolume = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [value => !isNumberWithOptionalFormatting(value), R.always(INTEGER)],
    [R.lte(R.__, 0), R.always(GT_VAL('0'))],
    [
      R.lt(R.__, totalDistributedVolume),
      R.always(GTE_VAL(formatNumberWithoutDecimals(totalDistributedVolume))),
    ],
    [R.T, R.always('')],
  ]);

  const today = startOfDay(new Date());
  const validStartDate = isBefore(parseISO(availableDate), today) ? today : parseISO(availableDate);

  const validateRequestedDate = R.cond([
    [value => isBefore(value, parseISO(availableDate)), R.always(DATE_GTE('available date'))],
    [value => isBefore(value, today), R.always(DATE_GTE_NOW)],
    [R.T, R.always('')],
  ]);

  const MAX_COMMENTS_CHAR = 255;

  const validateComments = R.cond([
    [value => !isLessThan(value, MAX_COMMENTS_CHAR), R.always(LT_VAL_CHAR(MAX_COMMENTS_CHAR))],
    [R.T, R.always('')],
  ]);

  const [validationMessages, setValidationMessages] = useState({
    comments: validateComments(shipmentUpdatesProp('comments')),
    requestedDate: validateRequestedDate(parseISO(shipmentUpdatesProp('requestedDate'))),
    volume: '',
  });

  useEffect(() => {
    setAreAllUpdatesValid(Object.values(validationMessages).every(value => R.isEmpty(value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMessages.comments, validationMessages.requestedDate, validationMessages.volume]);

  const validationMessagesProp = R.prop(R.__, validationMessages);

  const sanitizeVolume = R.compose(R.replace(/[^0-9]/g, ''), R.path(['target', 'value']));

  const handleVolumeChange = R.compose(
    setShipmentUpdates,
    R.assoc('volume'),
    formatNumberWithoutDecimals,
    R.tap(R.compose(setValidationMessages, R.assoc('volume'), validateVolume)),
    sanitizeVolume
  );

  const handleAgreementCodeChange = R.compose(
    setShipmentUpdates,
    R.assoc('agreementCode'),
    R.path(['target', 'value'])
  );

  const handleCommentsChange = R.compose(
    setShipmentUpdates,
    R.assoc('comments'),
    R.path(['target', 'value'])
  );

  const handleBookingOriginChange = (_, value) => {
    setShipmentUpdates(
      R.pipe(
        R.assoc('bookingOrigin', value),
        R.assoc('locationName', value.defaultLocationName)
      )(shipmentUpdates)
    );
  };

  return (
    <Box sx={infoContainerStyle}>
      <DataDisplayItem label="Origin" value={origin} />
      <Autocomplete
        disableClearable
        disabled={
          R.or(isFetchingBookingOrigins, R.equals(bookingOriginOptions.length, 1)) ||
          hasAcceptPermissions ||
          !onEditPage ||
          !hasAffiliateShipper ||
          R.equals('Unconfirmed', status)
        }
        getOptionLabel={option => R.defaultTo('', R.prop('bookingOriginName', option))}
        isOptionEqualToValue={R.eqProps('bookingOriginId')}
        onChange={handleBookingOriginChange}
        options={bookingOriginOptions}
        renderInput={renderInput({
          label: 'Booking Origin',
          length: bookingOriginOptions.length || 0,
          loading: isFetchingBookingOrigins,
        })}
        value={shipmentUpdatesProp('bookingOrigin')}
      />
      <TextField
        disabled={!onEditPage || !isBatchOwner || hasAcceptPermissions}
        error={R.not(R.isEmpty(validationMessagesProp('volume'))) || isDistributionsVolumeInvalid}
        helperText={
          isDistributionsVolumeInvalid
            ? 'Shipment volume cannot be less than the sum of associated distributions'
            : validationMessagesProp('volume')
        }
        label="Shipment Volume"
        onChange={handleVolumeChange}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
          },
        }}
        value={shipmentUpdatesProp('volume') ?? ''}
      />
      {onEditPage && !isBatchOwner && !R.equals('Unconfirmed', status) && (
        <Typography sx={{ mt: '-20px' }} variant="caption">
          Contact the Batch Owner to change the requested volume.
        </Typography>
      )}
      <DatePicker
        disabled={!onEditPage || isRequestDateDisabled || hasAcceptPermissions}
        disableMaskedInput
        inputFormat="MMMM dd, yyyy"
        label="Requested Date"
        maxDate={add(validStartDate, { days: 30 })}
        minDate={validStartDate}
        onAccept={() => setIsDatePickerOpen(false)}
        onChange={date => {
          setShipmentUpdates(R.assoc('requestedDate', format(date, "yyyy-MM-dd'T'HH:mm:ss")));
          setValidationMessages(R.assoc('requestedDate', ''));
        }}
        onClose={() => setIsDatePickerOpen(false)}
        open={isDatePickerOpen}
        openTo="day"
        slotProps={{
          textField: {
            error:
              R.not(R.isEmpty(validationMessagesProp('requestedDate'))) &&
              onEditPage &&
              !isRequestDateDisabled,
            helperText:
              onEditPage && !isRequestDateDisabled && validationMessagesProp('requestedDate'),
            inputProps: { readOnly: true },
            onClick: () => setIsDatePickerOpen(true),
          },
        }}
        value={parseISO(shipmentUpdatesProp('requestedDate'))}
      />
      <TextField
        disabled={!onEditPage || !isBatchOwner || hasAcceptPermissions}
        label="Agreement Code"
        onChange={handleAgreementCodeChange}
        value={shipmentUpdatesProp('agreementCode') ?? ''}
      />
      <TextField
        disabled={
          !onEditPage ||
          !hasAffiliateShipper ||
          R.equals('Unconfirmed', status) ||
          hasAcceptPermissions
        }
        helperText="Optional"
        label="Comments"
        multiline
        onChange={handleCommentsChange}
        rows={4}
        slotProps={{
          htmlInput: {
            style: {
              padding: 1,
            },
          },
        }}
        value={shipmentUpdatesProp('comments') || ''}
      />
    </Box>
  );
};

EditShipment.propTypes = {
  availableDate: PropTypes.string.isRequired,
  batchOwnerName: PropTypes.string.isRequired,
  bookingOriginOptions: PropTypes.arrayOf(
    PropTypes.shape({
      bookingOriginId: PropTypes.number,
      bookingOriginName: PropTypes.string,
    })
  ).isRequired,
  hasAcceptPermissions: PropTypes.bool.isRequired,
  isDistributionsVolumeInvalid: PropTypes.bool.isRequired,
  isFetchingBookingOrigins: PropTypes.bool.isRequired,
  onEditPage: PropTypes.bool.isRequired,
  origin: PropTypes.string.isRequired,
  setAreAllUpdatesValid: PropTypes.func.isRequired,
  setShipmentUpdates: PropTypes.func.isRequired,
  shipmentUpdates: PropTypes.shape({
    bookingOrigin: PropTypes.shape({
      bookingOriginId: PropTypes.number.isRequired,
      bookingOriginName: PropTypes.string.isRequired,
    }),
    requestedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    volume: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  shipperName: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  totalDistributedVolume: PropTypes.number.isRequired,
};

export default EditShipment;
